// File: src/ai/estimators/kilnCapacityEstimator.js
// Description: Basic kiln capacity estimator (space units) for load planning.

/**
 * Estimate kiln load status and suggestions.
 * @param {object|null} kiln Kiln object from KilnContext.
 * @param {Array<{ id?: string, name?: string, quantity?: number, spaceUnitsPerPiece?: number, dueDate?: string }>} loadItems
 */
export function estimateKilnLoad(kiln, loadItems) {
  if (!kiln) {
    return {
      totalSpaceUnits: 0,
      capacity: null,
      loadRatio: 0,
      status: "noKiln",
      suggestions: [],
    };
  }

  const capacity = kiln.usableSpaceUnits ?? null;
  const totalSpaceUnits = (Array.isArray(loadItems) ? loadItems : []).reduce(
    (sum, item) => sum + (Number(item?.quantity) || 0) * (Number(item?.spaceUnitsPerPiece) || 0),
    0
  );

  let loadRatio = 0;
  if (capacity && capacity > 0) {
    loadRatio = totalSpaceUnits / capacity;
  }

  const suggestions = [];

  if (!capacity) {
    suggestions.push({
      type: "info",
      message: "Set usable space units for this kiln to get capacity guidance.",
    });
  } else {
    if (loadRatio > 1.05) {
      suggestions.push({
        type: "overCapacity",
        message: "This firing is over kiln capacity. Consider splitting into multiple firings.",
      });
    } else if (loadRatio < 0.7) {
      suggestions.push({
        type: "underFilled",
        message:
          "This firing is significantly under-filled. Consider adding more work to use the kiln more efficiently.",
      });
    } else if (loadRatio < 0.9) {
      suggestions.push({
        type: "slightlyUnderFilled",
        message: "There is some spare space. You may be able to add a small extra batch.",
      });
    } else if (loadRatio > 0.95) {
      suggestions.push({
        type: "ideal",
        message: "This firing is very well packed.",
      });
    }
  }

  return {
    totalSpaceUnits,
    capacity,
    loadRatio,
    suggestions,
  };
}

/**
 * Derive a rough usable space unit estimate from kiln volume.
 * @param {object|null} kiln
 * @returns {number|null}
 */
export function estimateUsableSpaceUnitsFromKiln(kiln) {
  if (!kiln) return null;

  let litres = kiln.capacityLitres ? Number(kiln.capacityLitres) : null;

  if (
    !litres &&
    kiln.internalWidthCm &&
    kiln.internalDepthCm &&
    kiln.internalHeightCm
  ) {
    const w = Number(kiln.internalWidthCm) || 0;
    const d = Number(kiln.internalDepthCm) || 0;
    const h = Number(kiln.internalHeightCm) || 0;
    const volumeCm3 = w * d * h;
    litres = volumeCm3 / 1000; // cm^3 -> litres
  }

  if (!litres) return null;

  // Simple heuristic: assume ~60% usable packing volume, 1L ≈ 1 unit, rounded to nearest 10.
  const derated = litres * 0.6;
  return Math.round(derated / 10) * 10;
}
